import {
  useMutation,
  useQueryClient,
  queryOptions,
} from "@tanstack/react-query";
import { authenticatedRequest } from "@/lib/graphql/client";
import {
  ADMIN_HANDBOOKS,
  ADMIN_HANDBOOK_BY_ID,
} from "@/lib/graphql/queries/handbook";
import {
  ADMIN_CREATE_HANDBOOK,
  ADMIN_UPDATE_HANDBOOK,
  ADMIN_PUBLISH_HANDBOOK,
  ADMIN_UNPUBLISH_HANDBOOK,
  ADMIN_DELETE_HANDBOOK,
} from "@/lib/graphql/mutations/handbook";
import type {
  CreateHandbookInput,
  HandbookDetail,
  HandbookFilters,
  HandbookMutationResult,
  HandbooksResult,
  UpdateHandbookInput,
} from "@/types/handbook";
import type { MutationResult, PaginationInput } from "@/types/common";
import { toast } from "sonner";

export const handbookKeys = {
  all: ["handbook"] as const,
  lists: () => [...handbookKeys.all, "list"] as const,
  list: (filters: HandbookFilters & PaginationInput) =>
    [...handbookKeys.lists(), filters] as const,
  details: () => [...handbookKeys.all, "detail"] as const,
  detail: (id: string) => [...handbookKeys.details(), id] as const,
};

export function handbooksQueryOptions(
  filters: HandbookFilters,
  pagination: PaginationInput,
) {
  return queryOptions({
    queryKey: handbookKeys.list({ ...filters, ...pagination }),
    queryFn: async () => {
      const data = await authenticatedRequest<{
        adminHandbooks: HandbooksResult;
      }>(ADMIN_HANDBOOKS, {
        input: {
          page: pagination.page,
          limit: pagination.limit,
          status: filters.status || undefined,
          tag: filters.tag || undefined,
          search: filters.search || undefined,
        },
      });
      return data.adminHandbooks;
    },
    staleTime: 30_000,
  });
}

export function handbookByIdQueryOptions(handbookId: string) {
  return queryOptions({
    queryKey: handbookKeys.detail(handbookId),
    queryFn: async () => {
      const data = await authenticatedRequest<{
        adminHandbookById: HandbookDetail;
      }>(ADMIN_HANDBOOK_BY_ID, { handbookId });
      return data.adminHandbookById;
    },
    staleTime: 30_000,
  });
}

function assertMutationSuccess<T extends { success: boolean; message?: string }>(
  result: T,
  fallback: string,
): T {
  if (!result.success) {
    throw new Error(result.message || fallback);
  }
  return result;
}

export function useCreateHandbook() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateHandbookInput) => {
      const data = await authenticatedRequest<{
        adminCreateHandbook: HandbookMutationResult;
      }>(ADMIN_CREATE_HANDBOOK, { input });
      return assertMutationSuccess(data.adminCreateHandbook, "생성 실패");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: handbookKeys.lists() });
      toast.success("자료집 페이지를 생성했습니다");
    },
    onError: (e) => {
      toast.error(extractMessage(e, "생성 실패"));
    },
  });
}

export function useUpdateHandbook() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateHandbookInput) => {
      const data = await authenticatedRequest<{
        adminUpdateHandbook: HandbookMutationResult;
      }>(ADMIN_UPDATE_HANDBOOK, { input });
      return assertMutationSuccess(data.adminUpdateHandbook, "수정 실패");
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: handbookKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: handbookKeys.detail(vars.handbookId),
      });
      toast.success("수정사항을 저장했습니다");
    },
    onError: (e) => {
      toast.error(extractMessage(e, "수정 실패"));
    },
  });
}

export function usePublishHandbook() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (handbookId: string) => {
      const data = await authenticatedRequest<{
        adminPublishHandbook: HandbookMutationResult;
      }>(ADMIN_PUBLISH_HANDBOOK, { handbookId });
      return assertMutationSuccess(data.adminPublishHandbook, "발행 실패");
    },
    onSuccess: (_, handbookId) => {
      queryClient.invalidateQueries({ queryKey: handbookKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: handbookKeys.detail(handbookId),
      });
      toast.success("발행했습니다");
    },
    onError: (e) => {
      toast.error(extractMessage(e, "발행 실패"));
    },
  });
}

export function useUnpublishHandbook() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (handbookId: string) => {
      const data = await authenticatedRequest<{
        adminUnpublishHandbook: HandbookMutationResult;
      }>(ADMIN_UNPUBLISH_HANDBOOK, { handbookId });
      return assertMutationSuccess(data.adminUnpublishHandbook, "발행 취소 실패");
    },
    onSuccess: (_, handbookId) => {
      queryClient.invalidateQueries({ queryKey: handbookKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: handbookKeys.detail(handbookId),
      });
      toast.success("발행을 취소했습니다");
    },
    onError: (e) => {
      toast.error(extractMessage(e, "발행 취소 실패"));
    },
  });
}

export function useDeleteHandbook() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (handbookId: string) => {
      const data = await authenticatedRequest<{
        adminDeleteHandbook: MutationResult;
      }>(ADMIN_DELETE_HANDBOOK, { handbookId });
      return assertMutationSuccess(data.adminDeleteHandbook, "삭제 실패");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: handbookKeys.lists() });
      toast.success("삭제했습니다");
    },
    onError: (e) => {
      toast.error(extractMessage(e, "삭제 실패"));
    },
  });
}

function extractMessage(err: unknown, fallback: string): string {
  if (err instanceof Error) return err.message || fallback;
  if (typeof err === "string") return err;
  return fallback;
}
